import React, { type Ref, useState, useCallback, useRef, useMemo } from 'react';

import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import type { IssueToCreate } from '../common/types.tsx';
import InlineCreateForm, {
	type Props as InlineCreateFormProps,
} from './inline-create-form/index.tsx';
import {
	useInlineIssueCreateEntryPoint,
	useInlineIssueCreateAdditionalFieldsEntryPoint,
} from './inline-create-form/utils.tsx';
import InlineCreateTrigger from './inline-create-trigger/index.tsx';

export type HandlerArgs = {
	projectKey: string | null;
	defaultAssigneeId?: string;
	minHierarchyLevel?: number;
	maxHierarchyLevel?: number;
};

export type FormPropsType = Pick<
	InlineCreateFormProps,
	| 'initialIssueDraft' // Remove when cleaning up `memoize_inline_create_handlers`
	| 'getInitialIssueDraft'
	| 'onDraftUpdate'
	| 'onCancel'
	| 'entryPointSubject'
	| 'additionalFieldsEntryPointSubject'
>;

export type TriggerPropsType = {
	onClick: () => void;
	ref: Ref<HTMLButtonElement>;
};

type ReturnType = {
	isFormVisible: boolean;
	entryPointActions: EntryPointActions;
	additionalFieldsEntryPointActions?: EntryPointActions;
	formProps: FormPropsType;
	triggerProps: TriggerPropsType;
};

export type Props = Omit<InlineCreateFormProps, 'entryPointSubject'> & HandlerArgs;

export const useInlineCreateHandlers = ({
	projectKey,
	defaultAssigneeId,
	minHierarchyLevel,
	maxHierarchyLevel,
}: HandlerArgs): ReturnType => {
	const [isFormVisible, setIsFormVisible] = useState(false);
	const formIssueDraftRef = useRef<Partial<IssueToCreate>>();

	const { entryPointActions, entryPointReferenceSubject } = useInlineIssueCreateEntryPoint(
		projectKey,
		minHierarchyLevel,
		maxHierarchyLevel,
	);

	const ref = useRef<HTMLElement>(null);
	const buttonRef = useEntryPointButtonTrigger(entryPointActions);

	const handleTriggerClick = useCallback(() => {
		setIsFormVisible(true);
	}, []);

	const handleFormCancel = useCallback(() => {
		setIsFormVisible(false);
		ref.current?.focus();
	}, []);

	const onDraftUpdate = useCallback((issueDraft: Partial<IssueToCreate>) => {
		formIssueDraftRef.current = issueDraft;
	}, []);

	const {
		entryPointActions: additionalFieldsEntryPointActions,
		entryPointReferenceSubject: additionalFieldsEntryPointSubject,
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
	} = useInlineIssueCreateAdditionalFieldsEntryPoint({
		projectKey,
		defaultAssigneeId,
	});
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const additionalFieldsRef = useEntryPointButtonTrigger(additionalFieldsEntryPointActions);
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const mergedRefWithAdditionalFields = useMergeRefs(ref, buttonRef, additionalFieldsRef);

	if (fg('memoize_inline_create_handlers')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const formProps = useMemo<FormPropsType>(
			() => ({
				initialIssueDraft: formIssueDraftRef.current,
				getInitialIssueDraft: () => formIssueDraftRef.current,
				onDraftUpdate,
				onCancel: handleFormCancel,
				entryPointSubject: entryPointReferenceSubject,
				additionalFieldsEntryPointSubject,
			}),
			[
				additionalFieldsEntryPointSubject,
				entryPointReferenceSubject,
				handleFormCancel,
				onDraftUpdate,
			],
		);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const triggerProps = useMemo<TriggerPropsType>(
			() => ({
				onClick: handleTriggerClick,
				ref: mergedRefWithAdditionalFields,
			}),
			[handleTriggerClick, mergedRefWithAdditionalFields],
		);

		return {
			isFormVisible,
			entryPointActions,
			additionalFieldsEntryPointActions,
			formProps,
			triggerProps,
		};
	}

	return {
		isFormVisible,
		entryPointActions,
		additionalFieldsEntryPointActions,
		formProps: {
			initialIssueDraft: formIssueDraftRef.current,
			onDraftUpdate,
			onCancel: handleFormCancel,
			entryPointSubject: entryPointReferenceSubject,
			additionalFieldsEntryPointSubject,
		},
		triggerProps: {
			onClick: handleTriggerClick,
			ref: mergedRefWithAdditionalFields,
		},
	};
};

const InlineCreate = ({ projectKey, minHierarchyLevel, ...formProps }: Props) => {
	const {
		isFormVisible,
		formProps: formPropsAdditional,
		triggerProps,
	} = useInlineCreateHandlers({ projectKey, minHierarchyLevel });

	return isFormVisible ? (
		<InlineCreateForm {...formProps} {...formPropsAdditional} />
	) : (
		<InlineCreateTrigger {...triggerProps} />
	);
};

export default InlineCreate;
